import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

interface RecaptchaProps {
  onVerify: (token: string | null) => void;
  reset?: boolean;
  onReset?: () => void;
}

const Recaptcha: React.FC<RecaptchaProps> = ({ reset, onVerify, onReset }) => {
  const [error, setError] = useState<string | null>(null);
  // This state variable is crucial for forcing a re-render/reset
  const [recaptchaInstanceKey, setRecaptchaInstanceKey] = useState(0);

  useEffect(() => {
    // If the parent component signals a reset, increment the key
    // This will cause the ReCAPTCHA component to be unmounted and re-mounted
    if (reset) {
      setRecaptchaInstanceKey(prevKey => prevKey + 1);
      // Immediately call onVerify with null to clear the token in the parent
      onVerify(null);
      // Notify the parent that the reset has been triggered
      if (onReset) {
        onReset();
      }
    }
  }, [reset, onVerify, onReset]);

  const handleVerify = (token: string | null) => {
    onVerify(token);
    setError(null); // Clear any previous error on verification attempt
  };

  const handleExpired = () => {
    onVerify(null);
    setError("ReCAPTCHA challenge expired. Please verify again.");
  };

  const handleError = (err: any) => {
    console.error("ReCAPTCHA error:", err);
    onVerify(null);
    setError("ReCAPTCHA failed to load or verify. Please try again.");
  };

  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  if (!sitekey || sitekey.length === 0) {
    return <div>ReCAPTCHA is not configured. Please set NEXT_PUBLIC_RECAPTCHA_SITE_KEY.</div>;
  }

  return (
    <div>
      {error && <div style={{ color: "red", marginBottom: '10px' }}>{error}</div>}
      <ReCAPTCHA
        // Changing this key forces the component to remount, effectively resetting it
        key={recaptchaInstanceKey}
        sitekey={sitekey}
        onChange={handleVerify}
        onExpired={handleExpired}
        onErrored={handleError}
      />
    </div>
  );
};

export default Recaptcha;